<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, MapPinned } from 'lucide-svelte';
  import { hrWorkZonesApi, type WorkZoneBody } from '$lib/api/hr';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import RowActions from '$lib/components/ui/RowActions.svelte';
  import type { WorkZone } from '$lib/types';

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['hr-work-zones'],
    queryFn: () => hrWorkZonesApi.list()
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['hr-work-zones'] });
  }

  function minutesToTime(minutes: number): string {
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  }

  function timeToMinutes(value: string): number {
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  }

  const emptyForm = (): WorkZoneBody => ({
    name: '',
    latitude: 0,
    longitude: 0,
    radius: 100,
    active: true,
    all_users: false,
    description: '',
    google_maps_url: '',
    start_minute: 600,
    end_minute: 1080,
    grace_minutes: 20
  });

  let editing = $state<WorkZone | null>(null);
  let showForm = $state(false);
  let form = $state<WorkZoneBody>(emptyForm());
  let saving = $state(false);
  let parsing = $state(false);
  let error = $state('');

  function openCreate() {
    editing = null;
    form = emptyForm();
    error = '';
    showForm = true;
  }

  function openEdit(z: WorkZone) {
    editing = z;
    form = {
      name: z.name,
      latitude: z.latitude,
      longitude: z.longitude,
      radius: z.radius,
      active: z.active,
      all_users: z.all_users,
      description: z.description,
      google_maps_url: '',
      start_minute: z.start_minute,
      end_minute: z.end_minute,
      grace_minutes: z.grace_minutes
    };
    error = '';
    showForm = true;
  }

  async function parseUrl() {
    const url = form.google_maps_url?.trim();
    if (!url) return;
    parsing = true;
    error = '';
    try {
      const coords = await hrWorkZonesApi.parseMapsUrl(url);
      form.latitude = coords.lat;
      form.longitude = coords.lng;
      toast.success('Coordinates parsed from Google Maps URL.');
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      parsing = false;
    }
  }

  async function save() {
    if (!form.name.trim()) {
      error = 'Name is required.';
      return;
    }
    if (form.radius <= 0) {
      error = 'Radius must be greater than 0.';
      return;
    }
    if (form.start_minute < 0 || form.start_minute >= 1440 || form.end_minute < 0 || form.end_minute >= 1440 || form.end_minute <= form.start_minute) {
      error = 'Set a valid same-day schedule with an end time after the start time.';
      return;
    }
    if (form.grace_minutes < 0 || form.grace_minutes > 180) {
      error = 'Grace must be between 0 and 180 minutes.';
      return;
    }
    saving = true;
    error = '';
    try {
      if (editing) await hrWorkZonesApi.update(editing.id, form);
      else await hrWorkZonesApi.create(form);
      refresh();
      showForm = false;
      toast.success(editing ? 'Site updated.' : 'Site created.');
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  async function remove(z: WorkZone) {
    if (!confirm(`Delete site "${z.name}"?`)) return;
    try {
      await hrWorkZonesApi.remove(z.id);
      refresh();
      toast.success('Site deleted.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }
</script>

<div class="tym-page">
  <Toolbar>
    {#snippet actions()}
      <button type="button" class="btn-primary" onclick={openCreate}>
        <Plus class="h-4 w-4" /> New site
      </button>
    {/snippet}
  </Toolbar>

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load sites.</div>
  {:else if query.data}
    <DataTable
      mobileCards
      mobileColumns={['name', 'active', 'radius', 'assigned']}
      columns={[
        { key: 'name', label: 'Name', cell: nameCell },
        { key: 'radius', label: 'Radius', cell: radiusCell },
        { key: 'schedule', label: 'Schedule', cell: scheduleCell },
        { key: 'all_users', label: 'All users', cell: allUsersCell },
        { key: 'active', label: 'Active', cell: activeCell },
        { key: 'assigned', label: 'Assigned', cell: assignedCell },
        { key: 'actions', label: '', align: 'right' as const, cell: actionsCell }
      ]}
      rows={query.data}
      rowKey={(z) => z.id}
      emptyTitle="No sites yet"
      emptyBody="Add a geofenced location to let employees punch in on-site."
    />
  {/if}

  {#if showForm}
    <Modal title={editing ? 'Edit site' : 'New site'} onClose={() => (showForm = false)}>
      <div class="space-y-4">
        <div>
          <div class="label">Name</div>
          <input type="text" class="input" aria-label="Name" bind:value={form.name} />
        </div>

        <div class="grid grid-cols-3 gap-3">
          <div><div class="label">Start</div><input type="time" class="input" aria-label="Start time" value={minutesToTime(form.start_minute)} onchange={(e) => (form.start_minute = timeToMinutes(e.currentTarget.value))} /></div>
          <div><div class="label">End</div><input type="time" class="input" aria-label="End time" value={minutesToTime(form.end_minute)} onchange={(e) => (form.end_minute = timeToMinutes(e.currentTarget.value))} /></div>
          <div><div class="label">Grace (minutes)</div><input type="number" min="0" max="180" class="input" aria-label="Grace minutes" bind:value={form.grace_minutes} /></div>
        </div>

        <div>
          <div class="label">Paste Google Maps URL (optional)</div>
          <div class="flex gap-2">
            <input type="text" class="input" placeholder="https://maps.google.com/..." bind:value={form.google_maps_url} />
            <button type="button" class="btn-secondary shrink-0" disabled={parsing} onclick={parseUrl}>
              <MapPinned class="h-4 w-4" /> {parsing ? 'Parsing...' : 'Parse'}
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <div class="label">Latitude</div>
            <input type="number" step="any" class="input" aria-label="Latitude" bind:value={form.latitude} />
          </div>
          <div>
            <div class="label">Longitude</div>
            <input type="number" step="any" class="input" aria-label="Longitude" bind:value={form.longitude} />
          </div>
        </div>

        <div>
          <div class="label">Radius (meters)</div>
          <input type="number" min="1" class="input" aria-label="Radius (meters)" bind:value={form.radius} />
        </div>

        <div>
          <div class="label">Description</div>
          <input type="text" class="input" bind:value={form.description} />
        </div>

        <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
          <input type="checkbox" bind:checked={form.active} class="h-4 w-4 accent-toyama-teal" />
          Active
        </label>
        <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
          <input type="checkbox" bind:checked={form.all_users} class="h-4 w-4 accent-toyama-teal" />
          All users may punch in here (skip per-user assignment)
        </label>

        {#if error}<div class="tym-error-state">{error}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (showForm = false)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={save}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}
</div>

{#snippet nameCell(z: WorkZone)}
  <span class="font-medium text-navy">{z.name}</span>
{/snippet}
{#snippet radiusCell(z: WorkZone)}
  <span class="muted">{z.radius}m</span>
{/snippet}
{#snippet scheduleCell(z: WorkZone)}
  <span class="muted">{minutesToTime(z.start_minute)}–{minutesToTime(z.end_minute)} · {z.grace_minutes}m grace</span>
{/snippet}
{#snippet allUsersCell(z: WorkZone)}
  <span class="muted">{z.all_users ? 'Yes' : 'No'}</span>
{/snippet}
{#snippet activeCell(z: WorkZone)}
  <span class="inline-flex items-center gap-1.5 {z.active ? 'text-navy' : 'text-dark-gray'}">
    <StatusLamp tone={z.active ? 'good' : 'idle'} />
    {z.active ? 'Active' : 'Inactive'}
  </span>
{/snippet}
{#snippet assignedCell(z: WorkZone)}
  <span class="muted">{z.assigned_count ?? 0}</span>
{/snippet}
{#snippet actionsCell(z: WorkZone)}
  <RowActions items={[{ label: 'Edit', onClick: () => openEdit(z) }, { label: 'Delete site', onClick: () => remove(z), danger: true }]} />
{/snippet}
